import {gql} from '@apollo/client';
import {getTestProps} from '@benchling/js-test-utils';
import {Button, Group, Modal, Stack} from '@mantine/core';
import React, {useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import {trackEvent} from 'analytics/Analytics';
import type {CoreObjectId} from 'app-core/framework/CoreObjectConstants';
import {getApiBaseForObjectType, getObjectType} from 'app-core/framework/CoreObjectIntrospectionUtils';
import {convertToCollaborationCreateOrUpdates} from 'auth/PermissionUtil';
import CollaborationManagementContent, {Policy} from 'collaborator-components/CollaborationManagementContent';
import PoliceableDAO from 'collaborator-components/PoliceableDAO';
import useCollaborationManagementState from 'collaborator-components/useCollaborationManagementState';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {buildSharingAnalyticsMetrics, collaborationToAnalyticsParty} from 'llm/chat-library/SharingAnalytics';
import notifications, {Response} from 'notifications';
import InlineBanner, {InlineBannerType} from 'ui-library/banners/InlineBanner';
import {CenteredSpinner} from 'ui-library/Spinner';
import {isJqXHR} from 'util/Net';

import {
  AgentFoundryPoliciesQuery,
  AgentFoundryPoliciesQueryVariables,
} from './__generated/AgentFoundryPoliciesQuery';

// `policyType: policy` scopes this to the `AGENT_FOUNDRY_*` system policies, excluding the
// tenant's data policies. Unlike Routine, AgentFoundry's `get_item_type()` is `ItemType.OTHER`, a
// `DataPolicy`-supported item type, so `resourceType: AGENT_FOUNDRY` alone would also match those.
export const AGENT_FOUNDRY_POLICIES_QUERY = gql`
  query AgentFoundryPoliciesQuery @engComponent(name: LLM) @skipAudit {
    policyListing(resourceType: AGENT_FOUNDRY, policyType: policy) {
      policies {
        __typename
        id
        name
        isHidden
      }
    }
  }
`;

interface AgentFoundryAccessModalProps {
  agentFoundryId: string;
  onRequestClose: () => void;
}

/**
 * Modal for managing a foundry's collaborators, opened via the "Manage access" button on
 * AgentFoundryPage. Rendered read-only for anyone without canManageCollaborators, rather than
 * being hidden outright (mirroring RoutineAccessTab).
 */
export default function AgentFoundryAccessModal({
  agentFoundryId,
  onRequestClose,
}: AgentFoundryAccessModalProps): JSX.Element {
  const [collaborationState, collaborationActions] = useCollaborationManagementState(agentFoundryId);
  const policyResult = useBenchlingQuery<AgentFoundryPoliciesQuery, AgentFoundryPoliciesQueryVariables>(
    AGENT_FOUNDRY_POLICIES_QUERY
  );
  const [saving, setSaving] = useState(false);
  const [canSave, setCanSave] = useState(true);

  const hasPendingChanges = useMemo(
    () => !isEqual(collaborationState.collaborations, collaborationState.initialCollaborations),
    [collaborationState.collaborations, collaborationState.initialCollaborations]
  );

  if (
    collaborationState.queryResultType === QueryResultType.ERROR ||
    policyResult.type === QueryResultType.ERROR
  ) {
    return (
      <Modal opened={true} onClose={onRequestClose} title="Manage access" size="xl">
        <InlineBanner
          bannerType={InlineBannerType.ERROR}
          text="Could not load foundry access"
          supportingInfo="Try reloading the page."
        />
      </Modal>
    );
  }
  if (
    collaborationState.queryResultType !== QueryResultType.SUCCESS ||
    policyResult.type !== QueryResultType.SUCCESS
  ) {
    return (
      <Modal opened={true} onClose={onRequestClose} title="Manage access" size="xl">
        <CenteredSpinner />
      </Modal>
    );
  }

  const canManageCollaborators = collaborationState.policeable?.permissions.admin ?? false;
  const readOnly = saving || !canManageCollaborators;
  const systemPolicies = policyResult.data.policyListing.policies satisfies Array<Policy>;

  if (systemPolicies.length === 0) {
    return (
      <Modal opened={true} onClose={onRequestClose} title="Manage access" size="xl">
        <InlineBanner
          bannerType={InlineBannerType.WARNING}
          text="Foundry access management is currently unavailable."
        />
      </Modal>
    );
  }

  const systemPolicyIds = new Set(systemPolicies.map((policy) => policy.id));
  // The owner's access comes from the tenant's admin data policy, not one of the system policies
  // above, so it must be added here too or PolicyDropdown shows "None" instead of its name.
  const ownerPolicies = collaborationState.collaborations
    .filter((c) => c.fromOwnership && !systemPolicyIds.has(c.policy.id))
    .map((c) => c.policy);
  const policies = [...systemPolicies, ...new Map(ownerPolicies.map((p) => [p.id, p])).values()];
  const unavailablePolicyIds = ownerPolicies.map((p) => p.id);

  const handleSave = async (): Promise<void> => {
    setSaving(true);
    const apiBase = getApiBaseForObjectType(getObjectType({id: agentFoundryId as CoreObjectId}));
    try {
      await PoliceableDAO.updateCollaborations(
        agentFoundryId,
        convertToCollaborationCreateOrUpdates(collaborationState.collaborations),
        apiBase
      );
      const sharingMetrics = buildSharingAnalyticsMetrics(
        collaborationState.initialCollaborations
          .filter((collaboration) => !collaboration.fromOwnership)
          .map((collaboration) => collaborationToAnalyticsParty(collaboration)),
        collaborationState.collaborations
          .filter((collaboration) => !collaboration.fromOwnership)
          .map((collaboration) => collaborationToAnalyticsParty(collaboration))
      );
      trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.SUBMITTED_AGENT_FOUNDRY_ACCESS_CHANGES, {
        agentFoundryId,
        ...sharingMetrics,
      });
      notifications.success('Successfully updated foundry access.');
      onRequestClose();
    } catch (response) {
      // Surface request failures while preserving the success-only analytics event above.
      if (!isJqXHR(response)) {
        throw response;
      }
      notifications.notifyError(response as Response);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal opened={true} onClose={onRequestClose} title="Manage access" size="xl">
      <Stack spacing="md" {...getTestProps({component: 'AgentFoundryAccessModal', element: 'root'})}>
        <CollaborationManagementContent
          {...collaborationState}
          {...collaborationActions}
          policies={policies}
          unavailablePolicyIds={unavailablePolicyIds}
          allowEditableOwner={false}
          isReadOnly={readOnly}
          renderAuditorColumn={false}
          modalRender={true}
          setCanSave={setCanSave}
        />
        <Group position="right">
          <Button
            variant="tertiary"
            disabled={saving}
            onClick={onRequestClose}
            {...getTestProps({component: 'AgentFoundryAccessModal', element: 'closeButton'})}
          >
            {canManageCollaborators ? 'Cancel' : 'Close'}
          </Button>
          {canManageCollaborators && (
            <Button
              loading={saving}
              disabled={!canSave || saving || !hasPendingChanges}
              onClick={handleSave}
              {...getTestProps({component: 'AgentFoundryAccessModal', element: 'saveButton'})}
            >
              Save changes
            </Button>
          )}
        </Group>
      </Stack>
    </Modal>
  );
}
